import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { CotMeCounters, CotMeFamily, CotMePerson, MeResponse, ParentVerification } from '../types/community'
import {
  computeFlags,
  deriveRole,
  resolveAccountHolderId,
  type CotRole,
  type PersonFlags,
} from '../lib/personDisplay'
import { getMe } from '../lib/api/me'
import { getActingSession, switchProfile, type ActingSessionStatus } from '../lib/api/actingSession'
import { CotApiError } from '../lib/api/client'
import { useAuth } from './AuthContext'
import { useToast } from './ToastContext'
import { PinPromptModal, ProfileChooser } from '../components/profile/ProfileSwitchGate'
import { pendingReturnPath } from '../lib/returnPath'

const EMPTY_COUNTERS: CotMeCounters = { pendingApprovals: 0, openReports: 0, onlineFriends: 0 }

export interface PersonContextValue {
  loading: boolean
  error: string | null
  /** /me returned 404 family_not_found — resume the signup wizard. */
  needsOnboarding: boolean
  verification?: ParentVerification
  family: CotMeFamily | null
  people: CotMePerson[]
  activePerson: CotMePerson | null
  accountHolderId: string
  setActivePerson: (id: string) => void
  role: CotRole
  flags: PersonFlags
  guardianFirst: string
  isSolo: boolean
  isFresh: boolean
  counters: CotMeCounters
  refreshing?: boolean
  refresh: () => Promise<unknown> | void
}

const PersonContext = createContext<PersonContextValue | undefined>(undefined)

export function usePerson(): PersonContextValue {
  const ctx = useContext(PersonContext)
  if (!ctx) throw new Error('usePerson must be used within a PersonProvider')
  return ctx
}

export function PersonProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, token, getSessionGeneration } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { flash } = useToast()
  const mounted = useRef(true)
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])

  // Guardian-PIN prompt state (R12) — a person we are trying to switch UP to, awaiting the PIN.
  const [pinTarget, setPinTarget] = useState<CotMePerson | null>(null)
  const [pinError, setPinError] = useState<string | null>(null)
  const [switchingId, setSwitchingId] = useState<string | null>(null)
  const [transitioning, setTransitioning] = useState(false)
  const [identityError, setIdentityError] = useState<string | null>(null)
  const switchInFlight = useRef(false)

  const query = useQuery<MeResponse, CotApiError>({
    queryKey: ['community', 'me'],
    queryFn: getMe,
    enabled: isAuthenticated,
    retry: false,
  })

  // Acting-session status is the SERVER truth for who we are acting as, and whether R13 requires a
  // choice. Retry:false so a not-onboarded 404 doesn't loop.
  const sessionQuery = useQuery({
    queryKey: ['community', 'actingSession'],
    queryFn: getActingSession,
    enabled: isAuthenticated,
    retry: false,
  })

  const me = query.data ?? null
  const accountHolderId = me ? resolveAccountHolderId(me) : ''
  const serverActingId = sessionQuery.data?.actingPersonId ?? null
  const choiceRequired = sessionQuery.data?.choiceRequired ?? false

  const currentOperation = useCallback(() => {
    const generation = getSessionGeneration()
    return () => mounted.current && getSessionGeneration() === generation && !!token && sessionStorage.getItem('cot_jwt_token') === token
  }, [getSessionGeneration, token])

  // A confirmed server switch is a cache boundary. Hide domain observers while discarding the
  // previous actor's requests/data; keep only the family structure needed for account recovery.
  const applyActingStatus = useCallback(async (status: ActingSessionStatus, isCurrent: () => boolean) => {
    if (!isCurrent()) return
    setTransitioning(true)
    setIdentityError(null)
    try {
      await queryClient.cancelQueries({ queryKey: ['community'] })
      if (!isCurrent()) return
      queryClient.removeQueries({ predicate: (entry) => entry.queryKey[0] === 'community' && !['me', 'actingSession'].includes(String(entry.queryKey[1])) })
      queryClient.setQueryData<MeResponse>(['community', 'me'], (previous) => previous ? {
        ...previous, counters: { ...EMPTY_COUNTERS }, people: previous.people.map((person) => ({ ...person, online: false })),
      } : previous)
      queryClient.setQueryData(['community', 'actingSession'], status)
      setPinTarget(null)
      setPinError(null)
      navigate(pendingReturnPath() ?? '/')
      // staleTime:0 forces a real refresh even though we just seeded neutral recovery data.
      await queryClient.fetchQuery({ queryKey: ['community', 'me'], queryFn: getMe, staleTime: 0, retry: false })
    } finally {
      if (isCurrent()) setTransitioning(false)
    }
  }, [navigate, queryClient])

  const recoverActingIdentity = useCallback(async () => {
    const isCurrent = currentOperation()
    if (!isCurrent()) return
    setTransitioning(true)
    setIdentityError(null)
    try {
      await queryClient.cancelQueries({ queryKey: ['community'] })
      if (!isCurrent()) return
      const status = await queryClient.fetchQuery({ queryKey: ['community', 'actingSession'], queryFn: getActingSession, staleTime: 0, retry: false })
      await applyActingStatus(status, isCurrent)
    } catch {
      if (isCurrent()) {
        setIdentityError('Could not confirm the active profile. Try again.')
        setTransitioning(false)
      }
    }
  }, [applyActingStatus, queryClient, currentOperation])

  const doSwitch = useCallback(
    async (personId: string, pin?: string) => {
      if (switchInFlight.current) return
      const isCurrent = currentOperation()
      if (!isCurrent()) return
      switchInFlight.current = true
      setSwitchingId(personId)
      let status: ActingSessionStatus
      try {
        status = await switchProfile(personId, pin)
      } catch (err) {
        if (!isCurrent()) return
        const code = err instanceof CotApiError ? err.message : ''
        const person = me?.people.find((p) => p.id === personId) ?? null
        if (code === 'pin_required') {
          setPinTarget(person)
          setPinError(null)
        } else if (code === 'pin_invalid') {
          setPinTarget((prev) => prev ?? person)
          setPinError('That PIN is not correct.')
        } else if (code === 'pin_rate_limited') {
          setPinTarget((prev) => prev ?? person)
          setPinError('Too many attempts. Wait a little and try again.')
        } else if (code === 'pin_not_set') {
          setPinTarget(null)
          flash('Set a guardian PIN in settings first.')
        } else if (!(err instanceof CotApiError) || !err.status || err.status >= 500) {
          // A lost response does not prove the server rejected the switch. Re-read its identity.
          await recoverActingIdentity()
        } else {
          setPinTarget(null)
          flash('Could not switch profile. Try again.')
        }
        switchInFlight.current = false
        setSwitchingId(null)
        return
      }
      try {
        await applyActingStatus(status, isCurrent)
      } catch {
        // /me now owns the retryable error. The successful switch is never rolled back locally.
      } finally {
        if (isCurrent()) {
          switchInFlight.current = false
          setSwitchingId(null)
        }
      }
    },
    [me, applyActingStatus, recoverActingIdentity, flash, currentOperation],
  )

  const value = useMemo<PersonContextValue>(() => {
    const people = me?.people ?? []
    const activePerson = people.find((p) => p.id === serverActingId) ?? null
    const holderPerson = people.find((p) => p.id === accountHolderId) ?? null
    const status = (query.error as CotApiError | null)?.status
    const needsOnboarding = query.isError && status === 404 && query.error?.message === 'family_not_found'

    return {
      loading: transitioning || (isAuthenticated && (query.isLoading || (!needsOnboarding && sessionQuery.isLoading))),
      error: identityError ?? (query.isError && !needsOnboarding ? (query.error?.message ?? 'Could not load your account.') : sessionQuery.isError && !needsOnboarding ? sessionQuery.error.message : null),
      needsOnboarding,
      verification: me?.verification,
      family: me?.family ?? null,
      people,
      activePerson,
      accountHolderId,
      // Person switch is now a SERVER-held operation (R11); switching up prompts for the PIN (R12).
      setActivePerson: (id: string) => {
        if (id === serverActingId) return
        void doSwitch(id)
      },
      role: deriveRole(activePerson?.id ?? null, accountHolderId),
      flags: computeFlags(activePerson),
      guardianFirst: holderPerson?.firstName ?? '',
      isSolo: me?.family.isSolo ?? false,
      isFresh: me?.family.fresh ?? false,
      counters: switchingId || identityError || query.isError || !sessionQuery.isSuccess ? EMPTY_COUNTERS : me?.counters ?? EMPTY_COUNTERS,
      refreshing: transitioning || query.isFetching || sessionQuery.isFetching,
      refresh: () => identityError ? recoverActingIdentity() : Promise.all([query.refetch(), sessionQuery.refetch()]),
    }
  }, [me, serverActingId, accountHolderId, isAuthenticated, query, sessionQuery, doSwitch, switchingId, transitioning, identityError, recoverActingIdentity])

  // R13 — a family with managed profiles and no established session must CHOOSE before proceeding.
  const chooserActive = isAuthenticated && !query.isLoading && !query.isError && !identityError && !transitioning && choiceRequired && pinTarget === null

  return (
    <PersonContext.Provider value={value}>
      {transitioning ? <div role="status" className="flex min-h-screen items-center justify-center bg-paper-page px-5 text-sm text-ink-muted">Loading the selected profile…</div> : chooserActive ? (
        <ProfileChooser people={me?.people ?? []} onPick={(id) => void doSwitch(id)} busyId={switchingId} />
      ) : (
        children
      )}
      {pinTarget && (
        <PinPromptModal
          person={pinTarget}
          error={pinError}
          busy={switchingId === pinTarget.id}
          onSubmit={(pin) => void doSwitch(pinTarget.id, pin)}
          onCancel={() => {
            setPinTarget(null)
            setPinError(null)
          }}
        />
      )}
    </PersonContext.Provider>
  )
}

/** Build a fully-formed context value for tests (renderWithProviders). */
export function makeFixturePersonValue(overrides: Partial<PersonContextValue> = {}): PersonContextValue {
  const holder: CotMePerson = {
    id: 'holder',
    firstName: 'Dana',
    kind: 'Guardian',
    supervision: 'NotApplicable',
    ageYears: 39,
    handle: null,
    avatarTintIndex: 0,
    online: true,
    isAccountHolder: true,
  }
  const family: CotMeFamily = {
    id: 'fam',
    displayName: 'The Rowes',
    isSolo: false,
    standing: 'New',
    fresh: true,
    accountHolderPersonId: 'holder',
  }
  const base: PersonContextValue = {
    loading: false,
    error: null,
    needsOnboarding: false,
    family,
    people: [holder],
    activePerson: holder,
    accountHolderId: 'holder',
    setActivePerson: () => {},
    role: 'parent',
    flags: computeFlags(holder),
    guardianFirst: 'Dana',
    isSolo: false,
    isFresh: true,
    counters: { ...EMPTY_COUNTERS },
    refresh: () => {},
  }
  return { ...base, ...overrides }
}

export { PersonContext }
